import { Show } from '../types/Show';

export class TimeZoneUtils {
  /**
   * Get current time in London timezone (handles BST/GMT automatically)
   */
  static getCurrentLondonTime(): Date {
    // Create a new Date object that represents the current time in London
    const now = new Date();
    
    // Use Intl.DateTimeFormat to get London time components
    const londonTime = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).formatToParts(now);

    // Reconstruct Date object from London time components
    const year = parseInt(londonTime.find(part => part.type === 'year')?.value || '0');
    const month = parseInt(londonTime.find(part => part.type === 'month')?.value || '0') - 1; // Month is 0-indexed
    const day = parseInt(londonTime.find(part => part.type === 'day')?.value || '0');
    const hour = parseInt(londonTime.find(part => part.type === 'hour')?.value || '0');
    const minute = parseInt(londonTime.find(part => part.type === 'minute')?.value || '0');
    const second = parseInt(londonTime.find(part => part.type === 'second')?.value || '0');

    return new Date(year, month, day, hour, minute, second);
  }

  /**
   * Parse show date/time string and convert to Date object
   * Format: "YYYY-MM-DD#HH:mm:ss"
   */
  static parseShowDateTime(dateTimeString: string): Date {
    const [datePart, timePart] = dateTimeString.split('#');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute, second] = timePart.split(':').map(Number);
    
    // Create date assuming it's in London timezone
    return new Date(year, month - 1, day, hour, minute, second);
  }

  /**
   * Check if a show is currently live based on London time
   * Uses date_end_time if available, otherwise assumes 2-hour duration
   */
  static isShowCurrentlyLive(show: Show, currentLondonTime?: Date): boolean {
    const now = currentLondonTime || this.getCurrentLondonTime();
    const showStart = this.parseShowDateTime(show.date_start_time);
    
    let showEnd: Date;
    
    // Use date_end_time if available (preferred method)
    if (show.date_end_time) {
      showEnd = this.parseShowDateTime(show.date_end_time);
    } else {
      // Fallback: use duration or assume 2 hours
      const showDurationMs = show.duration ? show.duration * 1000 : 2 * 60 * 60 * 1000; // 2 hours default
      showEnd = new Date(showStart.getTime() + showDurationMs);
    }
    
    // Check if current time is between show start and end
    const isLive = now >= showStart && now <= showEnd;
    
    // Debug logging
    if (process.env.NODE_ENV === 'development') {
      // Debug logging removed for production
    }
    
    return isLive;
  }

  /**
   * Get today's upcoming shows (shows that haven't started yet today)
   */
  static getTodaysUpcomingShows(shows: Show[]): Show[] {
    const now = this.getCurrentLondonTime();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);

    return shows.filter(show => {
      const showStart = this.parseShowDateTime(show.date_start_time);
      
      // Show is today and hasn't started yet
      return showStart >= now && showStart >= today && showStart < tomorrow;
    }).sort((a, b) => {
      // Sort by start time
      const timeA = this.parseShowDateTime(a.date_start_time);
      const timeB = this.parseShowDateTime(b.date_start_time);
      return timeA.getTime() - timeB.getTime();
    });
  }

  /**
   * Get current live show from schedule
   */
  static getCurrentLiveShow(shows: Show[]): Show | null {
    const now = this.getCurrentLondonTime();
    
    // Find show that's currently broadcasting
    const liveShow = shows.find(show => this.isShowCurrentlyLive(show, now));
    
    if (liveShow) {
      // Mark as live and return
      return {
        ...liveShow,
        isLive: true
      };
    }
    
    return null;
  }

  /**
   * Format show date/time for display
   */
  static formatShowDateTime(show: Show): { date: string; time: string } {
    const showDate = this.parseShowDateTime(show.date_start_time);
    
    return {
      date: showDate.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: '2-digit',
        year: '2-digit'
      }),
      time: showDate.toLocaleTimeString('en-GB', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false
      })
    };
  }

  /**
   * Check if a date is today in London timezone
   */
  static isToday(date: Date): boolean {
    const now = this.getCurrentLondonTime();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const checkDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    return today.getTime() === checkDate.getTime();
  }

  /**
   * Check if a date is yesterday in London timezone
   */
  static isYesterday(date: Date): boolean {
    const now = this.getCurrentLondonTime();
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    const checkDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    return yesterday.getTime() === checkDate.getTime();
  }

  /**
   * Get timezone offset info for debugging
   */
  static getTimezoneInfo(): { 
    londonTime: string; 
    localTime: string; 
    isDST: boolean;
    offset: string;
  } {
    const now = new Date();
    const londonTime = this.getCurrentLondonTime();
    
    // Check if London is currently in DST (BST)
    const january = new Date(now.getFullYear(), 0, 1);
    const july = new Date(now.getFullYear(), 6, 1);
    const janOffset = january.getTimezoneOffset();
    const julOffset = july.getTimezoneOffset();
    const isDST = Math.max(janOffset, julOffset) !== now.getTimezoneOffset();
    
    return {
      londonTime: londonTime.toISOString(),
      localTime: now.toISOString(),
      isDST,
      offset: isDST ? 'BST (UTC+1)' : 'GMT (UTC+0)'
    };
  }
}
